import React, { useEffect, useMemo, useState } from "react";
import { Button, DatePicker, Segmented, Select, Space, Typography } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs, { type Dayjs } from "dayjs";
import { useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import api from "@/services/api.service";
import type { ReportParams } from "@/services/reportDocuments.service";
import ReportDocumentPanel from "@/components/finance/report-document/ReportDocumentPanel";
import PeriodRangeFilter from "@/components/finance/report-document/PeriodRangeFilter";
import { isoDate } from "@/components/finance/report-document/periodPresets";

const { Title, Text } = Typography;

type View = "register" | "ageing" | "exposure";

/** Supplier advances: register (period + balance status), ageing as at a date, and exposure by supplier. */
const SupplierAdvancesReportPage: React.FC = () => {
  const { t } = useTranslation("finance");
  const { t: tc } = useTranslation("common");
  const [searchParams, setSearchParams] = useSearchParams();
  const initialView =
    (["register", "ageing", "exposure"] as View[]).find(
      (v) => v === searchParams.get("view"),
    ) ?? "register";
  const [view, setView] = useState<View>(initialView);
  const [range, setRange] = useState<[Dayjs, Dayjs]>([
    dayjs().startOf("year"),
    dayjs(),
  ]);
  const [asOf, setAsOf] = useState<Dayjs>(dayjs());
  const [status, setStatus] = useState<string>("all");
  const [supplierId, setSupplierId] = useState<string | undefined>(
    searchParams.get("supplier_id") ?? undefined,
  );
  const [params, setParams] = useState<ReportParams | null>(null);

  const { data: suppliers = [] } = useQuery({
    queryKey: ["statement-suppliers"],
    queryFn: async () => {
      const res = await api.get<any>("/procurement/suppliers");
      const data = (res as any)?.data ?? res;
      return (
        Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : []
      ) as any[];
    },
  });
  const supplierOptions = useMemo(
    () =>
      suppliers.map((s) => ({
        value: s.id as string,
        label: `${s.supplier_name ?? ""}${s.supplier_code ? ` (${s.supplier_code})` : ""}`,
      })),
    [suppliers],
  );

  const generate = (v: View = view) => {
    const next: ReportParams = { view: v };
    if (supplierId) next.supplier_id = supplierId;
    if (v === "register") {
      next.from = isoDate(range[0]);
      next.to = isoDate(range[1]);
      next.status = status;
    } else if (v === "ageing") {
      next.as_of = isoDate(asOf);
    }
    setParams(next);
    setSearchParams(
      { view: v, ...(supplierId ? { supplier_id: supplierId } : {}) },
      { replace: true },
    );
  };

  useEffect(() => {
    generate(initialView);
  }, []);

  return (
    <div>
      <Title level={4}>{t("supplierAdvances")}</Title>
      <ReportDocumentPanel
        reportKey="supplier-advances"
        params={params}
        filters={
          <Space wrap size="middle" align="end">
            <Segmented<View>
              value={view}
              onChange={(v) => {
                setView(v);
                generate(v);
              }}
              options={[
                { value: "register", label: t("register") },
                { value: "ageing", label: t("ageing") },
                { value: "exposure", label: t("exposure") },
              ]}
            />
            {view === "register" && (
              <>
                <PeriodRangeFilter
                  value={range}
                  onChange={setRange}
                  initialPreset="ytd"
                  label={t("paidBetween")}
                />
                <Space direction="vertical" size={4}>
                  <Text type="secondary">{t("balance")}</Text>
                  <Select
                    value={status}
                    onChange={setStatus}
                    style={{ width: 160 }}
                    options={[
                      { value: "all", label: t("all") },
                      { value: "open", label: t("openOnly") },
                      { value: "adjusted", label: t("fullyAdjusted") },
                    ]}
                  />
                </Space>
              </>
            )}
            {view === "ageing" && (
              <Space direction="vertical" size={4}>
                <Text type="secondary">{t("asAt")}</Text>
                <DatePicker
                  value={asOf}
                  onChange={(d) => d && setAsOf(d)}
                  format="DD-MM-YYYY"
                  allowClear={false}
                />
              </Space>
            )}
            <Space direction="vertical" size={4}>
              <Text type="secondary">{t("supplier")}</Text>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder={t("allSuppliers")}
                value={supplierId}
                onChange={setSupplierId}
                options={supplierOptions}
                style={{ width: 280 }}
              />
            </Space>
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => generate()}
            >
              {tc("generate")}
            </Button>
          </Space>
        }
      />
    </div>
  );
};

export default SupplierAdvancesReportPage;
